import { ComponentRef, Injectable, Injector, Type, ViewContainerRef } from '@angular/core';
import { SopoAlertComponent } from '../components/sopo-alert/sopo-alert.component';
import { SopoConfirmComponent } from '../components/sopo-confirm/sopo-confirm.component';
import { SopoDialogComponent } from '../components/sopo-dialog/sopo-dialog.component';
import { DEFAULT_SOPO_DIALOG_SETTINGS, SopoDialogRef, SopoDialogSettings, SOPO_DIALOG_DATA } from '../interfaces/dialog';
import { SopoAlertSettings, DEFAULT_SOPO_ALERT_SETTINGS } from '../interfaces/alert';
import { SopoClubJoinConfirmSettings, SopoConfirmSettings } from '../interfaces/confirm';
import { SopoStringInputComponent } from '../components/sopo-string-input/sopo-string-input.component';
import { SopoStringInputSettings } from '../interfaces/string-input';
import { SopoNumberInputComponent } from '../components/sopo-number-input/sopo-number-input.component';
import { SopoNumberInputSettings } from '../interfaces/number-input';
import { firstValueFrom, take } from 'rxjs';
import { SopoDoubleInputComponent, SopoDoubleInputSettings } from '../components/sopo-double-input/sopo-double-input.component';
import { SopoPhoneNumberInputSettings } from '../interfaces/phone-number-input';
import { SopoPhoneNumberInputComponent } from '../components/sopo-phone-number-input/sopo-phone-number-input.component';
import { SopoTreeButtonConfirmSettings } from '../interfaces/tree-button-confirm';
import { SopoTreeButtonConfirmComponent } from '../components/sopo-tree-button-confirm/sopo-tree-button-confirm.component';
import { SopoMultiDaySettingsInputComponent } from '../components/sopo-multi-day-settings-input/sopo-multi-day-settings-input.component';
import { SopoMultiDaySettings } from '../interfaces/multi-day';
import { SopoSelectListSettings } from '../interfaces/select-list';
import { SopoSelectListComponent } from '../components/sopo-select-list/sopo-select-list.component';
import { GameStateId } from 'src/app/core/state/game/game.state';
import { GameDialogService } from 'src/app/game/page/game/gameDialog.service';
import { SopoVerifyDataSettings, SopoVerifyInfoPopupComponent } from '../components/sopo-verify-info/sopo-verify-info.component';
import { SopoVerifyAdditionalDataSettings, SopoVerifyAdditionalInfoComponent } from '../components/sopo-verify-additional-info/sopo-verify-additional-info.component';
import { SopoDepositFeeComponent, SopoDepositFeeSettings } from '../components/sopo-deposit-fee/sopo-deposit-fee.component';
import { SopoCoinflowShopComponent } from '../components/sopo-coinflow-shop/sopo-coinflow-shop.component';
import { ISopoCoinflowDialogSetting } from '../interfaces/coinflow';
import { ISopoTransactionHistoryFilterSettings } from '../interfaces/transaction-history-filter';
import { TransactionHistoryFilterComponent } from 'src/app/lobby/pages/cashier/components/transaction-history/transaction-history-filter/transaction-history-filter.component';
import { SopoClubJoinConfirmComponent } from '../components/sopo-club-join-confirm/sopo-club-join-confirm.component';
import { TableRestartDialogComponent } from 'src/app/game/component/table-restart-dialog/table-restart-dialog.component';
import { SopoConfirmCryptoWithdrawSettings, SopoConfirmCryptoWithdrawComponent } from '../components/sopo-confirm-crypto-withdraw/sopo-confirm-crypto-withdraw.component';
import { SopoSideBettingComponent, SopoSideBettingSettings } from '../components/sopo-side-betting/sopo-side-betting.component';
import { SopoDisableSideBettingComponent, SopoDisableSideBettingSettings } from '../components/sopo-disable-side-betting/sopo-disable-side-betting.component';
import { SopoSideBettingSuccessComponent, SopoSideBettingSuccessSettings } from '../components/sopo-side-betting-success/sopo-side-betting-success.component';
import { KYCRequiredDialogComponent } from 'src/app/shared/components/kyc-required-dialog/kyc-required-dialog.component';

export type ViewContainerName = 'app' | 'game' | 'game-container';
export const DEFAULT_DISMISS_AFTER_TIME = 5000;
@Injectable({
    providedIn: 'root',
})
export class SopoPopupService {
    private viewContainerRefs: { [index: string]: ViewContainerRef } = {};
    private openedDialogs: { [container: string]: SopoDialogRef[] } = {};
    private openedAlerts: { [container: string]: ComponentRef<SopoAlertComponent>[] } = {};
    private gameContainerRefs: { [index: GameStateId]: ViewContainerRef } = {};
    private openedGameDialogs: { [container: GameStateId]: SopoDialogRef[] } = {};
    private openedGameAlerts: { [container: GameStateId]: ComponentRef<SopoAlertComponent>[] } = {};
    private _abstractLoginOpened = false;

    constructor(
        private gameDialogService: GameDialogService,
    ) {}

    checkForViewContainerRefs(viewContainerName: ViewContainerName): boolean {
        return !!this.viewContainerRefs[viewContainerName];
    }

    getOpenedDialogs(viewContainerName: ViewContainerName, gameStateId?: GameStateId): number {
        if (viewContainerName === 'game-container') {
            return this.openedGameDialogs[gameStateId] ? this.openedGameDialogs[gameStateId].length : 0;
        } else {
            return this.openedDialogs[viewContainerName] ? this.openedDialogs[viewContainerName].length : 0;
        }
    }

    getOpenedAlerts(viewContainerName: ViewContainerName, gameStateId?: GameStateId): number {
        if (viewContainerName === 'game-container') {
            return this.openedGameAlerts[gameStateId] ? this.openedGameAlerts[gameStateId].length : 0;
        } else {
            return this.openedAlerts[viewContainerName] ? this.openedAlerts[viewContainerName].length : 0;
        }
    }

    setViewContainerRef(viewContainerName: ViewContainerName, viewContainerRef: ViewContainerRef, gameStateId?: GameStateId) {
        if (viewContainerName === 'game-container') {
            if (gameStateId === undefined) console.error("SetViewContainerRef for game-container with undefined GameStateId ");
            else this.gameContainerRefs[gameStateId] = viewContainerRef;
        } else {
            this.viewContainerRefs[viewContainerName] = viewContainerRef;
        }
    }

    removeViewContainerRef(viewContainerName: ViewContainerName, gameStateId?: GameStateId) {
        if (viewContainerName === 'game-container') {
            if (gameStateId === undefined) console.error("RemoveViewContainerRef for game-container with undefined GameStateId ");
            else if (this.gameContainerRefs[gameStateId]) {
                this.gameContainerRefs[gameStateId].clear();
                delete this.gameContainerRefs[gameStateId];
                this.clearGameViewContainer(gameStateId, true);
            }
        }
        else if (this.viewContainerRefs[viewContainerName]) {
            this.viewContainerRefs[viewContainerName].clear();
            delete this.viewContainerRefs[viewContainerName];
        }
    }

    showAlert(viewContainerName: ViewContainerName, alertSettings: SopoAlertSettings, gameStateId?: GameStateId): ComponentRef<SopoAlertComponent> {
        if (this._abstractLoginOpened) {
            viewContainerName = 'app';
            alertSettings.zIndex = 50001;
        }

        if (viewContainerName === 'game-container') {
            if (gameStateId === undefined || !this.viewContainerRefs['game']) {
                console.error('NO VIEW CONTAINER FOR GAMECONTAINER WITH GAMESTATEID: ', gameStateId);
                return;
            }
        }
        else if(!this.viewContainerRefs[viewContainerName]) {
            console.error('NO VIEW CONTAINER FOR: ', viewContainerName);
            return;
        }

        if (viewContainerName === 'game-container') alertSettings.zIndex = alertSettings.zIndex === undefined ? 1001 : alertSettings.zIndex;
        if (viewContainerName === 'game') alertSettings.zIndex = alertSettings.zIndex === undefined ? 1002 : alertSettings.zIndex;

        const settings: SopoAlertSettings = { ...DEFAULT_SOPO_ALERT_SETTINGS, ...alertSettings }

        if (!settings.backdropDismiss && settings.dismissAfter != undefined && settings.dismissAfter <= 0 && !settings.preventAutoDismiss) {
            settings.dismissAfter = 6000;
        }

        if (viewContainerName === 'game-container') settings.isGameContainer = true;

        let alertCustomInjector = Injector.create({
            providers: [{ provide: 'AlertSettings', useValue: settings }]
        });

        let componentRef: ComponentRef<SopoAlertComponent>;

        if (viewContainerName == 'game-container') {
            componentRef = this.viewContainerRefs['game'].createComponent(SopoAlertComponent, { injector: alertCustomInjector });
        } else {
            componentRef = this.viewContainerRefs[viewContainerName].createComponent(SopoAlertComponent, { injector: alertCustomInjector });
        }

        if (viewContainerName === 'game-container') {
            this.gameDialogService.changeSopoAlertClassOnViewChange(gameStateId, componentRef);
        }

        if (viewContainerName === 'game-container') {
            if (!this.openedGameAlerts[gameStateId]) this.openedGameAlerts[gameStateId] = [];
            this.openedGameAlerts[gameStateId].push(componentRef);
        } else {
            if (!this.openedAlerts[viewContainerName]) this.openedAlerts[viewContainerName] = [];
            this.openedAlerts[viewContainerName].push(componentRef);
        }

        const destroySubscription = componentRef.instance.getDestroyObservable().pipe(
            take(1),
        ).subscribe(destroy => {
            if (destroy) {
                componentRef.destroy();
            }
        });

        const sub = componentRef.instance.getAlertClosed().subscribe(() => {
            if (destroySubscription && !destroySubscription.closed) destroySubscription.unsubscribe();
            if (viewContainerName === 'game-container') {
                const alert = this.openedGameAlerts[gameStateId].findIndex(alert => alert === componentRef);
                if (alert >= 0) this.openedGameAlerts[gameStateId].splice(alert, 1);
            } else {
                const alert = this.openedAlerts[viewContainerName].findIndex(alert => alert === componentRef);
                if (alert >= 0) this.openedAlerts[viewContainerName].splice(alert, 1);
            }
            sub.unsubscribe();
        });

        componentRef.changeDetectorRef.detectChanges();

        return componentRef;
    }

    async promptForConfirmation(viewContainerName: ViewContainerName, confirmSettings: SopoConfirmSettings, gameStateId?: GameStateId): Promise<boolean> {
        const dialogRef = this.openDialog(viewContainerName, SopoConfirmComponent, confirmSettings, gameStateId);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForClubJoinConfirmation(viewContainerName: ViewContainerName, confirmClubJoinSettings: SopoClubJoinConfirmSettings, gameStateId?: GameStateId): Promise<boolean> {
        const dialogRef = this.openDialog(viewContainerName, SopoClubJoinConfirmComponent, confirmClubJoinSettings, gameStateId);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    openDialog(viewContainerName: ViewContainerName, component: Type<any>, dialogSettings?: SopoDialogSettings, gameStateId?: GameStateId): SopoDialogRef | undefined {
        if (this._abstractLoginOpened) {
            viewContainerName = 'app';
            dialogSettings.zIndex = 50001;
        }

        if (viewContainerName === 'game-container') {
            if (gameStateId === undefined || !this.viewContainerRefs['game']) {
                console.error('NO VIEW CONTAINER FOR GAMECONTAINER WITH GAMESTATEID: ', gameStateId);
                return;
            }
        }
        else if(!this.viewContainerRefs[viewContainerName]) {
            console.error('NO VIEW CONTAINER FOR: ', viewContainerName);
            return;
        }

        if (viewContainerName === 'game-container') dialogSettings.zIndex = dialogSettings.zIndex === undefined ? 1001 : dialogSettings.zIndex;
        if (viewContainerName === 'game') dialogSettings.zIndex = dialogSettings.zIndex === undefined ? 1002 : dialogSettings.zIndex;

        const settings: SopoDialogSettings = { ...DEFAULT_SOPO_DIALOG_SETTINGS, ...dialogSettings, insertedComponentType: component };

        if (viewContainerName === 'app') {
            settings.sCss.push('sopo-popup-app-max-height');
        }

        const dialogRef = new SopoDialogRef();

        dialogRef.doNotCloseOnClear = settings.doNotCloseOnClear;

        if (viewContainerName === 'game-container') {
            if (!this.openedGameDialogs[gameStateId]) this.openedGameDialogs[gameStateId] = [];
            this.openedGameDialogs[gameStateId].push(dialogRef);
        } else {
            if (!this.openedDialogs[viewContainerName]) this.openedDialogs[viewContainerName] = [];
            this.openedDialogs[viewContainerName].push(dialogRef);
        }

        if (viewContainerName === 'game-container') {
            settings.isGameContainer = true;
        }

        let dialogCustomInjector = Injector.create({
            providers: [
                { provide: SopoDialogSettings, useValue: settings },
                { provide: SopoDialogRef, useValue: dialogRef },
                { provide: SOPO_DIALOG_DATA, useValue: settings.data },
            ]
        });

        let dialogComponentRef: ComponentRef<SopoDialogComponent>;
        if (viewContainerName === 'game-container') {
            dialogComponentRef = this.viewContainerRefs['game'].createComponent(SopoDialogComponent, { injector: dialogCustomInjector });
        } else {
            dialogComponentRef = this.viewContainerRefs[viewContainerName].createComponent(SopoDialogComponent, { injector: dialogCustomInjector });
        }

        dialogRef.dialogComponentRef = dialogComponentRef;

        if (viewContainerName === 'game-container') {
            this.gameDialogService.changeSopoDialogClassOnViewChange(gameStateId, dialogRef);
        }

        const sub = dialogRef.afterClosed.subscribe(() => {
            if (viewContainerName === 'game-container') {
                const dialog = this.openedGameDialogs[gameStateId].findIndex(dialog => dialog === dialogRef);
                if (dialog >= 0) this.openedGameDialogs[gameStateId].splice(dialog, 1);
            } else {
                const dialog = this.openedDialogs[viewContainerName].findIndex(dialog => dialog === dialogRef);
                if (dialog >= 0) this.openedDialogs[viewContainerName].splice(dialog, 1);
            }
            // close the dialog
            dialogComponentRef.destroy();
            sub.unsubscribe();
        });

        dialogComponentRef.changeDetectorRef.detectChanges();
        return dialogRef;
    }

    async promptForString(viewContainerName: ViewContainerName, dialogSettings?: SopoStringInputSettings): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoStringInputComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForNumberAsString(viewContainerName: ViewContainerName, dialogSettings?: SopoStringInputSettings): Promise<any> {
        dialogSettings.data.numberOnly = true;
        const dialogRef = this.openDialog(viewContainerName, SopoStringInputComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForNumber(viewContainerName: ViewContainerName, dialogSettings?: SopoNumberInputSettings): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoNumberInputComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForDoubleInput(viewContainerName: ViewContainerName, dialogSettings?: SopoDoubleInputSettings): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoDoubleInputComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForPhoneNumber(viewContainerName: ViewContainerName, dialogSettings?: SopoPhoneNumberInputSettings, getDialogRef?: boolean): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoPhoneNumberInputComponent, dialogSettings);
        if (!dialogRef) return null;
        return getDialogRef ? dialogRef : firstValueFrom(dialogRef.afterClosed);
    }

    async promptForTreeButtonConfirm(viewContainerName: ViewContainerName, dialogSettings?: SopoTreeButtonConfirmSettings): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoTreeButtonConfirmComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForMultiDaySettings(viewContainerName: ViewContainerName, dialogSettings?: SopoMultiDaySettings): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoMultiDaySettingsInputComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForVerifyDataInput(viewContainerName: ViewContainerName, dialogSettings?: SopoVerifyDataSettings): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoVerifyInfoPopupComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForVerifyAdditionalDataInput(viewContainerName: ViewContainerName, dialogSettings?: SopoVerifyAdditionalDataSettings): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoVerifyAdditionalInfoComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    promptForSelectList<T>(viewContainerName: ViewContainerName, dialogSettings?: SopoSelectListSettings<T>): SopoDialogRef | null {
        const dialogRef = this.openDialog(viewContainerName, SopoSelectListComponent<T>, dialogSettings);
        if (!dialogRef) return null;
        return dialogRef;
    }

    async promptForSideBettingOptions(viewContainerName: ViewContainerName, dialogSettings?: SopoDisableSideBettingSettings): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoDisableSideBettingComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForSideBetting(viewContainerName: ViewContainerName, dialogSettings?: SopoSideBettingSettings, gameSateId?: GameStateId): Promise<SopoDialogRef | undefined> {
        return this.openDialog(viewContainerName, SopoSideBettingComponent, dialogSettings, gameSateId);
    }

    async openSideBetSuccess(viewContainerName: ViewContainerName, dialogSettings?: SopoSideBettingSuccessSettings, gameSateId?: GameStateId): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoSideBettingSuccessComponent, dialogSettings, gameSateId);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    public async coinflowInShop(viewContainerName: ViewContainerName, dialogSettings?: ISopoCoinflowDialogSetting): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoCoinflowShopComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForConfirmDepositFee(viewContainerName: ViewContainerName, dialogSettings?: SopoDepositFeeSettings): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoDepositFeeComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForAccountHistoryFilter(viewContainerName: ViewContainerName, dialogSettings?: ISopoTransactionHistoryFilterSettings): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, TransactionHistoryFilterComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async promptForConfirmCryptoWithdraw(viewContainerName: ViewContainerName, dialogSettings?: SopoConfirmCryptoWithdrawSettings): Promise<any> {
        const dialogRef = this.openDialog(viewContainerName, SopoConfirmCryptoWithdrawComponent, dialogSettings);
        if (!dialogRef) return null;
        return firstValueFrom(dialogRef.afterClosed);
    }

    async clearViewContainer(viewContainerName: ViewContainerName, gameStateId?: GameStateId): Promise<void> {
        if (viewContainerName === 'game-container') {
            if (gameStateId === undefined) console.error("ClearViewContainerRef for game-container with undefined GameStateId ");
            else await this.clearGameViewContainer(gameStateId);
        }
        else await this.clearRegularViewContainer(viewContainerName);
    }

    private async clearRegularViewContainer(viewContainerName: ViewContainerName): Promise<void> {
        let tasks = [];

        if (this.openedDialogs[viewContainerName]) {
            for (const openedDialog of this.openedDialogs[viewContainerName]) {
                if (!openedDialog.doNotCloseOnClear) tasks.push(openedDialog.close(null));
            };
        }

        if (this.openedAlerts[viewContainerName]) {
            for (const openedAlert of this.openedAlerts[viewContainerName]) {
                if (!openedAlert.instance.settings.doNotCloseOnClear) tasks.push(openedAlert.instance.dismiss());
            };
        }

        await Promise.allSettled(tasks);
    }

    private async clearGameViewContainer(gameStateId: GameStateId, ignoreDoNotCloseOnClear = false): Promise<void> {
        let tasks = []

        if (this.openedGameDialogs[gameStateId]) {
            for (const openedDialog of this.openedGameDialogs[gameStateId]) {
                if (!openedDialog.doNotCloseOnClear || ignoreDoNotCloseOnClear) tasks.push(openedDialog.close(null));
            };
        }

        if (this.openedGameAlerts[gameStateId]) {
            for (const openedAlert of this.openedGameAlerts[gameStateId]) {
                if (!openedAlert.instance.settings.doNotCloseOnClear || ignoreDoNotCloseOnClear) tasks.push(openedAlert.instance.dismiss());
            };
        }

        await Promise.allSettled(tasks);
    }

    isDialogOpen(viewContainerName: ViewContainerName, component: Type<any>): boolean {
        if(!this.openedDialogs[viewContainerName]) return false;
        return this.openedDialogs[viewContainerName].some(dialog => dialog.insertedComponentRef.componentType === component);
    }

    setAbstractWalletOpened(opened: boolean): void {
        this._abstractLoginOpened = opened;
    }

    async clearAllViewContainer(): Promise<void> {
        let tasks = [];

        let gameContainerRefs = Object.keys(this.gameContainerRefs);
        for (let i = 0; i < gameContainerRefs.length; i ++) {
            tasks.push(this.clearGameViewContainer(gameContainerRefs[i]));
        }

        let viewContainerRefs = Object.keys(this.viewContainerRefs);
        for (let i = 0; i < viewContainerRefs.length; i ++) {
            tasks.push(this.clearRegularViewContainer(viewContainerRefs[i] as ViewContainerName));
        }

        await Promise.allSettled(tasks);
    }

    openTableRestartDialog(viewContainerName: ViewContainerName, gameStateId: GameStateId, dialogSettings?: SopoDialogSettings): void {
        this.openDialog(viewContainerName, TableRestartDialogComponent, dialogSettings, gameStateId);
    }

    public openKYCRequiredDialog(viewContainerName: ViewContainerName, dialogSettings?: SopoDialogSettings): SopoDialogRef | null {
        const settings = dialogSettings ? { ...dialogSettings, sCss: [...(dialogSettings.sCss ?? [])] } : undefined;
        const dialogRef = this.openDialog(viewContainerName, KYCRequiredDialogComponent, settings);
        if (!dialogRef) return null;
        return dialogRef;
    }

    public getPromptForStringDialogRef(viewContainerName: ViewContainerName, dialogSettings?: SopoStringInputSettings): SopoDialogRef | null {
        const dialogRef = this.openDialog(viewContainerName, SopoStringInputComponent, dialogSettings);
        if (!dialogRef) return null;
        return dialogRef;
    }
}
